import * as _ from 'lodash-es';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {Side} from '../../../enums/side';
import {ExchangeAccountBase} from '../../../models/exchange-account';
import {Security} from '../../../models/security';
import {formatSecurityKey, isSecurityKeysEqual, parseSecurityKey, SecurityKey} from '../../../models/security-key';
import {
    CommonWidgetImplWithAccountService,
    CommonWidgetImplWithSecurityService,
    getSettingsWithUpdateField,
    hasSettingUpdateField,
    isWidgetSettingsUpdatesSecurityKey,
    WidgetSettingsWithSecurityKey
} from '../../../widgets/components/common-widget.implementation';
import {WidgetSettingsType} from '../../../widgets/components/widget-impl-settings-component-base';
import {AccountPermission} from '../../account-permissions';
import {AccountService} from '../../accounts';
import {Widget} from '../../layout/widget/widget';
import {WidgetSettings} from '../../layout/widget/widget-settings';
import {
    AspreaderSecurity,
    AspreaderSecurityLeg,
    isAsecurity,
    isAsecurityKey,
    parseAsecurityConfig
} from './aspreader-security';
import {AspreaderModel, getAsecurityLegsAccDictFix} from './aspreader-security-fixes';

export interface AsecurityLegsAccountDictRec {
    accountBase: ExchangeAccountBase;
    leverage: number;
    makerOnly: boolean;
    maxShow: number | null | undefined;
    maxUnfilled: number | null | undefined;
}

export interface AsecurityLegsAccountDict {
    [securityKeyStr: string]: AsecurityLegsAccountDictRec;
}

export interface AsecurityLegsAccountBuySellDictRec {
    accountBase: ExchangeAccountBase;
    buyLeverage: number;
    sellLeverage: number;
    makerOnly: boolean;
    maxShow: number;
    maxUnfilled: number;
}

export interface AsecurityLegsAccountBuySellDict {
    [securityKeyStr: string]: AsecurityLegsAccountBuySellDictRec;
}

interface AsecurityLegsAccBuySellDictSettings {
    legsAccountBuySellDict: AsecurityLegsAccountBuySellDict;
}

type WsettingsWithAsecurityLegsAccBuySellDict<WidgetType extends Widget = Widget> = WidgetSettingsType<WidgetType>
    & AsecurityLegsAccBuySellDictSettings & WidgetSettingsWithSecurityKey;

export const isAsecurityLegsAccountDictsEqual = (
    dict1: AsecurityLegsAccountDict | AsecurityLegsAccountBuySellDict,
    dict2: AsecurityLegsAccountDict | AsecurityLegsAccountBuySellDict
): boolean =>
    !!dict1 === !!dict2
    && (!dict1 || _.isEqual(dict1, dict2));

export const getAsecurityLegsAccDictCorrectionBySettings = (
    widgetImpl: CommonWidgetImplWithSecurityService<AspreaderModel & WidgetSettings> & CommonWidgetImplWithAccountService,
    settings: Partial<AspreaderModel>
): Observable<Partial<AspreaderModel>> => {
    return getAsecurityLegsAccDictFix(
        settings,
        widgetImpl.settings,
        {
            accountService:  widgetImpl.accountService,
            securityService: widgetImpl.securityService
        }
    );
};

export const getAsecurityLegsAccBuySellDictCorrectionBySettings = <S extends WsettingsWithAsecurityLegsAccBuySellDict>(
    widgetImpl: CommonWidgetImplWithSecurityService<S> & CommonWidgetImplWithAccountService,
    settings: Partial<S>
): Observable<Partial<S>> => {
    const isSecurityKeyChanged = isWidgetSettingsUpdatesSecurityKey(widgetImpl, settings);
    const isDictNotStrictlySet = !hasSettingUpdateField(settings, 'legsAccountBuySellDict')
        || !settings.legsAccountBuySellDict;

    const isCorrectionNeeded = isSecurityKeyChanged || isDictNotStrictlySet;

    let result$: Observable<Partial<S>>;

    if (isCorrectionNeeded) {
        const securityKey = getSettingsWithUpdateField(widgetImpl.settings, settings, 'securityKey');
        const dict = getSettingsWithUpdateField(widgetImpl.settings, settings, 'legsAccountBuySellDict');

        if (securityKey) {
            if (isAsecurityKey(securityKey)) {
                const asecurity$ = widgetImpl.securityService
                    .getSecurityBySecurityKey(securityKey) as Observable<AspreaderSecurity>;
                const accountService = widgetImpl.accountService;

                result$ = asecurity$.pipe(
                    map(asecurity => {
                        let stepResult: Partial<S>;

                        if (asecurity) {
                            const config = parseAsecurityConfig(asecurity);
                            const newDict: AsecurityLegsAccountBuySellDict = dict
                                ? correctAsecurityLegsAccountBuySellDict(securityKey, config.legs, dict, accountService)
                                : getDefaultAsecurityLegsAccountBuySellDict(securityKey, config.legs, accountService);

                            stepResult = {legsAccountBuySellDict: newDict} as Partial<S>;
                        }

                        return stepResult;
                    })
                );
            } else if (dict) {
                result$ = of<Partial<S>>({legsAccountBuySellDict: null} as Partial<S>);
            }
        }
    }

    return result$ || of(null);
};

const isValidLegAccountBase = (
    accountBase: ExchangeAccountBase,
    securityKey: SecurityKey,
    accountService: AccountService
): boolean => {
    const account = accountBase && accountService.getAccountByIdFromCache(accountBase.id);

    return account && securityKey.securityExchange === account.exchange
        && accountService.hasPermissionOnAccountId(account.id, AccountPermission.WRITE);
};

const getDefaultLegsAccountDictRec = (
    securityKeys: SecurityKey[],
    accountService: AccountService
): AsecurityLegsAccountDictRec => {
    const accountInfo = accountService.validateAccountInfoOrDefaultFromCache(
        undefined,
        undefined,
        {
            securityKeys,
            permission: AccountPermission.WRITE
        }
    );

    return {
        accountBase: accountInfo && accountInfo.accountBase,
        leverage:    accountInfo && accountInfo.leverage,
        makerOnly:   false,
        maxShow:     undefined,
        maxUnfilled: undefined
    };
};

const getDefaultLegsAccountBuySellDictRec = (
    securityKeys: SecurityKey[],
    accountService: AccountService
): AsecurityLegsAccountBuySellDictRec => {
    const accountInfo = accountService.validateAccountInfoOrDefaultFromCache(
        undefined,
        undefined,
        {
            securityKeys,
            permission: AccountPermission.WRITE
        }
    );

    return {
        accountBase:  accountInfo && accountInfo.accountBase,
        buyLeverage:  accountInfo && accountInfo.leverage, // TODO: Available leverages might be different per side
        sellLeverage: accountInfo && accountInfo.leverage, // TODO: Available leverages might be different per side
        makerOnly:    false,
        maxShow:      undefined,
        maxUnfilled:  undefined
    };
};

export const getDefaultAsecurityLegsAccountDict = (
    aSecurityKey: SecurityKey,
    legs: AspreaderSecurityLeg[],
    accountService: AccountService
): AsecurityLegsAccountDict => {
    const securityKeys = getAsecurityLegsSecurityKeys(legs);

    return securityKeys.reduce<AsecurityLegsAccountDict>(
        (acc, securityKey) => {
            if (securityKey) {
                acc[formatSecurityKey(securityKey)] = getDefaultLegsAccountDictRec(
                    [aSecurityKey, securityKey],
                    accountService
                );
            }

            return acc;
        },
        {}
    );
};

export const getDefaultAsecurityLegsAccountBuySellDict = (
    aSecurityKey: SecurityKey,
    legs: AspreaderSecurityLeg[],
    accountService: AccountService
): AsecurityLegsAccountBuySellDict => {
    const securityKeys = getAsecurityLegsSecurityKeys(legs);

    return securityKeys.reduce<AsecurityLegsAccountBuySellDict>(
        (acc, securityKey) => {
            if (securityKey) {
                acc[formatSecurityKey(securityKey)] = getDefaultLegsAccountBuySellDictRec(
                    [aSecurityKey, securityKey],
                    accountService
                );
            }

            return acc;
        },
        {}
    );
};

export const getAsecurityLegsSecurityKeys = (legs: AspreaderSecurityLeg[]): SecurityKey[] => {
    const keysDict: Record<string, boolean> = {};

    return (legs || []).reduce(
        (acc, leg) => {
            const {securityKey} = leg;
            const securityKeyStr = formatSecurityKey(securityKey);

            if (!keysDict[securityKeyStr]) {
                keysDict[securityKeyStr] = true;

                acc.push(securityKey);
            }

            return acc;
        },
        []
    );
};

export const correctAsecurityLegsAccountDict = (
    aSecurityKey: SecurityKey,
    legs: AspreaderSecurityLeg[],
    dict: AsecurityLegsAccountDict,
    accountService: AccountService
): AsecurityLegsAccountDict => {
    let newDict: AsecurityLegsAccountDict = dict;
    const legsSecurityKeys = getAsecurityLegsSecurityKeys(legs);
    const initNewDict = (): AsecurityLegsAccountDict =>
        newDict = (newDict === dict) ? {...dict} : newDict;
    const dictKeysToRemove = Object.keys(newDict);

    legsSecurityKeys.forEach(securityKey => {
        const securityKeyStr = formatSecurityKey(securityKey);
        const dictValue = newDict[securityKeyStr];

        if (!dictValue || !isValidLegAccountBase(dictValue.accountBase, securityKey, accountService)) {
            initNewDict()[securityKeyStr] = getDefaultLegsAccountDictRec(
                [aSecurityKey, securityKey],
                accountService
            );
        }

        if (dictValue) {
            _.remove(dictKeysToRemove, key => key === securityKeyStr);
        }
    });

    dictKeysToRemove.forEach(key => delete newDict[key]);

    return newDict;
};

export const correctAsecurityLegsAccountBuySellDict = (
    aSecurityKey: SecurityKey,
    legs: AspreaderSecurityLeg[],
    dict: AsecurityLegsAccountBuySellDict,
    accountService: AccountService
): AsecurityLegsAccountBuySellDict => {
    let newDict: AsecurityLegsAccountBuySellDict = dict;
    const legsSecurityKeys = getAsecurityLegsSecurityKeys(legs);
    const initNewDict = (): AsecurityLegsAccountBuySellDict =>
        newDict = (newDict === dict) ? {...dict} : newDict;
    const dictKeysToRemove = Object.keys(newDict);

    legsSecurityKeys.forEach(securityKey => {
        const securityKeyStr = formatSecurityKey(securityKey);
        const dictValue = newDict[securityKeyStr];

        if (!dictValue || !isValidLegAccountBase(dictValue.accountBase, securityKey, accountService)) {
            initNewDict()[securityKeyStr] = getDefaultLegsAccountBuySellDictRec(
                [aSecurityKey, securityKey],
                accountService
            );
        }

        if (dictValue) {
            _.remove(dictKeysToRemove, key => key === securityKeyStr);
        }
    });

    dictKeysToRemove.forEach(key => delete newDict[key]);

    return newDict;
};

export const getLegsAccountDictFromBuySellDict = (
    buySellDict: AsecurityLegsAccountBuySellDict,
    side: Side,
    security: AspreaderSecurity | Security
): AsecurityLegsAccountDict => {
    let result: AsecurityLegsAccountDict;

    if (buySellDict && isAsecurity(security)) {
        const asecurityConfig = parseAsecurityConfig(security);

        result = Object.keys(buySellDict).reduce<AsecurityLegsAccountDict>(
            (dictAcc, key) => {
                const rec: AsecurityLegsAccountBuySellDictRec = buySellDict[key];
                const securityKey = parseSecurityKey(key);
                const leg = asecurityConfig.legs.find(itLeg => isSecurityKeysEqual(itLeg.securityKey, securityKey));

                if (leg) {
                    dictAcc[key] = {
                        accountBase: rec.accountBase,
                        leverage:    (side === Side.BUY)
                                         ? rec.buyLeverage
                                         : rec.sellLeverage,
                        makerOnly:   !!rec.makerOnly,
                        maxShow:     leg.activeQuoting && _.isFinite(rec.maxShow) ? rec.maxShow : undefined,
                        maxUnfilled: leg.activeQuoting && _.isFinite(rec.maxUnfilled) ? rec.maxUnfilled : undefined
                    };
                }

                return dictAcc;
            },
            {}
        );
    }

    return result;
};
